import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useAuth } from 'src/contexts/auth'
import { FileDto } from 'src/dto/file.dto'
import { FileController } from 'src/controllers/file.controller'
import { useGetFileList } from 'src/hooks/api/file/useGetFileList'
import { useUploadFile } from 'src/hooks/api/file/mutations/useUploadFile'
import { useDeleteFile } from 'src/hooks/api/file/mutations/useDeleteFile'
import { GalleryPhoto, UploadingPhoto } from 'src/components/AnimalGallery/types'

const fileController = new FileController()

const ENTITY = 'Animal'

let uploadCounter = 0

export function useAnimalGallery(animalId?: string) {
  const { userFarm: farmId } = useAuth()
  const queryClient = useQueryClient()
  const blobUrlsRef = useRef<Set<string>>(new Set())

  const filesMapRef = useRef<Map<string, FileDto>>(new Map())
  const [uploadingPhotos, setUploadingPhotos] = useState<UploadingPhoto[]>([])

  const uploadMutation = useUploadFile(ENTITY, animalId)
  const deleteMutation = useDeleteFile(ENTITY, animalId)

  const { data: files, isLoading: isLoadingFiles } = useGetFileList(ENTITY, animalId)

  const photosQueryKey = ['animal-gallery-photos', farmId, animalId]

  const { data: photos, isFetching } = useQuery({
    queryKey: photosQueryKey,
    queryFn: async () => {
      if (!files || files.length === 0) return []

      filesMapRef.current = new Map(files.map((file) => [file.idArquivo, file]))

      return Promise.all(
        files.map(async (file): Promise<GalleryPhoto> => {
          const blob = await fileController.getFile(farmId!, ENTITY, animalId!, file.idArquivo)

          const url = URL.createObjectURL(blob)

          blobUrlsRef.current.add(url)

          return {
            id: file.idArquivo,
            url,
            description: file.nomeArquivo
          }
        })
      )
    },
    enabled: !!farmId && !!animalId && !!files
  })

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url)
      })

      blobUrlsRef.current.clear()
    }
  }, [])

  const appendPhotoToCache = useCallback(
    (photo: GalleryPhoto) => {
      queryClient.setQueryData<GalleryPhoto[]>(photosQueryKey, (old) => [...(old ?? []), photo])
    },
    [queryClient, photosQueryKey]
  )

  const removeUploadingPhoto = useCallback((id: string) => {
    setUploadingPhotos((prev) => prev.filter((p) => p.id !== id))
  }, [])

  const addPhotos = useCallback(
    async (files: File[]) => {
      if (!farmId || !animalId) return

      const items: UploadingPhoto[] = files.map((file) => ({
        id: `upload-${++uploadCounter}`,
        previewUrl: URL.createObjectURL(file),
        fileName: file.name,
        file,
        status: 'uploading' as const
      }))

      setUploadingPhotos((prev) => [...prev, ...items])

      const results = await Promise.allSettled(
        items.map(async (uploadingPhoto) => {
          const fileDto = await uploadMutation.mutateAsync(uploadingPhoto.file)

          filesMapRef.current.set(fileDto.idArquivo, fileDto)

          appendPhotoToCache({
            id: fileDto.idArquivo,
            url: uploadingPhoto.previewUrl,
            description: fileDto.nomeArquivo
          })

          setUploadingPhotos((prev) => prev.filter((p) => p.id !== uploadingPhoto.id))
        })
      )

      const errors = results.filter((r) => r.status === 'rejected')
      const successes = results.filter((r) => r.status === 'fulfilled')

      if (successes.length > 0) {
        const msg =
          successes.length === 1 ? 'Foto adicionada com sucesso!' : `${successes.length} fotos adicionadas com sucesso!`
        toastSyncSuccess(msg)
      }

      if (errors.length > 0) {
        setUploadingPhotos((prev) =>
          prev.map((p) => (p.status === 'uploading' ? { ...p, status: 'error' as const } : p))
        )
        const msg = errors.length === 1 ? 'Erro ao enviar 1 foto.' : `Erro ao enviar ${errors.length} fotos.`
        toast.error(msg)
      }
    },
    [farmId, animalId, uploadMutation, appendPhotoToCache]
  )

  const retryUpload = useCallback(
    async (photo: UploadingPhoto) => {
      if (!farmId || !animalId) return

      setUploadingPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, status: 'uploading' as const } : p)))

      try {
        const fileDto = await uploadMutation.mutateAsync(photo.file)

        filesMapRef.current.set(fileDto.idArquivo, fileDto)

        appendPhotoToCache({
          id: fileDto.idArquivo,
          url: photo.previewUrl,
          description: fileDto.nomeArquivo
        })

        setUploadingPhotos((prev) => prev.filter((p) => p.id !== photo.id))
      } catch {
        setUploadingPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, status: 'error' as const } : p)))
      }
    },
    [farmId, animalId, uploadMutation, appendPhotoToCache]
  )

  const removePhoto = useCallback(
    async (photoId: string) => {
      if (!farmId || !animalId) return

      const fileDto = filesMapRef.current.get(photoId)

      if (!fileDto) return

      const previousPhotos = queryClient.getQueryData<GalleryPhoto[]>(photosQueryKey) ?? []

      const removedPhoto = previousPhotos.find((photo) => photo.id === photoId)

      // Remove imediatamente da UI
      queryClient.setQueryData<GalleryPhoto[]>(
        photosQueryKey,
        previousPhotos.filter((photo) => photo.id !== photoId)
      )

      filesMapRef.current.delete(photoId)

      try {
        await deleteMutation.mutateAsync(fileDto)

        // libera apenas o blob removido
        if (removedPhoto?.url && removedPhoto.url.startsWith('blob:')) {
          URL.revokeObjectURL(removedPhoto.url)
          blobUrlsRef.current.delete(removedPhoto.url)
        }

        toastSyncSuccess('Foto removida com sucesso!')

        queryClient.invalidateQueries({
          queryKey: ['getFileList', ENTITY, animalId]
        })
      } catch {
        // rollback
        queryClient.setQueryData(photosQueryKey, previousPhotos)

        filesMapRef.current.set(photoId, fileDto)

        toast.error('Erro ao remover foto.')
      }
    },
    [farmId, animalId, deleteMutation, queryClient, photosQueryKey]
  )

  return {
    photos: photos ?? [],
    isLoading: isLoadingFiles && uploadingPhotos.length === 0,
    isRefreshing: isFetching && !isLoadingFiles,
    addPhotos,
    removePhoto,
    retryUpload,
    removeUploadingPhoto,
    uploadingPhotos
  }
}
